/**
 * AcceptInvitePage — "Set Your Password" page for the email invite flow.
 *
 * Flow:
 *   1. User clicks invite link in email → lands here with :token param
 *   2. Page shows a "Set Password" form (no login required)
 *   3. User enters + confirms password
 *   4. Calls POST /v1/invites/{token}/complete (public endpoint)
 *      → backend sets password, adds to household, marks invite accepted
 *   5. Frontend auto-signs in with email + password → redirects to dashboard
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiConfig } from '../../config/amplify';
import { BRAND } from '../../config/branding';

const AcceptInvitePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<'form' | 'submitting' | 'signing-in' | 'success' | 'error'>('form');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    // Validate
    if (password.length < 8) {
      setErrorMessage('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setStatus('submitting');

    try {
      // Call the public complete-invite endpoint (no auth token needed)
      const response = await fetch(`${apiConfig.baseUrl}/v1/invites/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || `Failed to complete invitation (${response.status})`);
      }

      const result = await response.json();

      // Auto sign-in with the email + password they just set
      setStatus('signing-in');
      await signIn(result.email, password);

      setStatus('success');
      setTimeout(() => navigate('/app', { replace: true }), 1500);
    } catch (err: unknown) {
      setStatus('error');
      if (err instanceof Error) {
        const msg = err.message;
        if (msg.includes('not found') || msg.includes('expired')) {
          setErrorMessage('This invitation has expired or is no longer valid. Please ask the household admin to send a new one.');
        } else if (msg.includes('already used')) {
          setErrorMessage('This invitation has already been used. Try signing in.');
        } else {
          setErrorMessage(msg);
        }
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    }
  };

  // Success state
  if (status === 'success') {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ maxWidth: 440 }}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'var(--color-success, #059669)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 16,
            }}>
              <span style={{ fontSize: 28, color: 'white' }}>✓</span>
            </div>
            <h2 style={{ margin: '0 0 8px', color: 'var(--color-text)' }}>Welcome!</h2>
            <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              Redirecting to your dashboard...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state (non-recoverable — expired/used token)
  if (status === 'error' && (errorMessage.includes('expired') || errorMessage.includes('already been used'))) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ maxWidth: 440 }}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'var(--color-error, #DC2626)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 16,
            }}>
              <span style={{ fontSize: 28, color: 'white' }}>✕</span>
            </div>
            <h2 style={{ margin: '0 0 12px', color: 'var(--color-text)' }}>
              Unable to accept invitation
            </h2>
            <p style={{
              color: 'var(--color-text-muted)', margin: '0 0 24px',
              lineHeight: 1.5, fontSize: 15,
            }}>
              {errorMessage}
            </p>
            <a
              href="/login"
              className="btn btn-primary"
              style={{
                display: 'inline-block', padding: '10px 24px',
                borderRadius: 8, textDecoration: 'none', fontWeight: 600,
              }}
            >
              Go to Sign In
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Form state (default) + submitting / signing-in / recoverable errors
  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: 440 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14,
            background: 'var(--color-primary, #2D6A4F)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 16,
          }}>
            <span style={{ fontSize: 28, color: 'white' }}>🍽️</span>
          </div>
          <h2 style={{ margin: '0 0 8px', color: 'var(--color-text)' }}>
            Set Your Password
          </h2>
          <p style={{ color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.5 }}>
            Choose a password to join your household on {BRAND.name}.
          </p>
        </div>

        {errorMessage && status === 'error' && (
          <div style={{
            background: 'var(--color-error, #DC2626)', color: 'white',
            padding: '10px 14px', borderRadius: 8, marginBottom: 16,
            fontSize: 14, lineHeight: 1.4,
          }}>
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{
              display: 'block', marginBottom: 6, fontWeight: 600,
              fontSize: 14, color: 'var(--color-text)',
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              autoFocus
              required
              minLength={8}
              disabled={status === 'submitting' || status === 'signing-in'}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 8,
                border: '1px solid var(--color-border, #E5E7EB)',
                fontSize: 15, boxSizing: 'border-box',
                background: 'var(--color-surface, white)',
                color: 'var(--color-text)',
              }}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{
              display: 'block', marginBottom: 6, fontWeight: 600,
              fontSize: 14, color: 'var(--color-text)',
            }}>
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Type it again"
              autoComplete="new-password"
              required
              minLength={8}
              disabled={status === 'submitting' || status === 'signing-in'}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 8,
                border: '1px solid var(--color-border, #E5E7EB)',
                fontSize: 15, boxSizing: 'border-box',
                background: 'var(--color-surface, white)',
                color: 'var(--color-text)',
              }}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={status === 'submitting' || status === 'signing-in'}
            style={{
              width: '100%', padding: '12px 24px', borderRadius: 8,
              fontWeight: 600, fontSize: 15, cursor: 'pointer',
            }}
          >
            {status === 'submitting'
              ? 'Setting up your account...'
              : status === 'signing-in'
                ? 'Signing you in...'
                : 'Set Password & Join'}
          </button>
        </form>

        <p style={{
          textAlign: 'center', marginTop: 20, fontSize: 13,
          color: 'var(--color-text-muted)',
        }}>
          Already have an account?{' '}
          <a href="/login" style={{ color: 'var(--color-primary, #2D6A4F)', fontWeight: 600 }}>
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
